import React, { useContext, useEffect, useState } from "react";
import "./Product.css";
import { ThemeContext } from "../../Contexts/ThemeContext";
import { toast } from "react-hot-toast";
import { addToDb } from "../../utilities/CartDb";
import { useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import ReactPixel from "react-facebook-pixel";
import StarRating from "../../Components/Ratings";

const Product = ({ product }) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [reviews, setReviews] = useState([]);

  const { cart, setCart } = useContext(ThemeContext);
  const navigate = useNavigate();

  const productImage =
    product?.images?.[0]?.src ||
    "https://via.placeholder.com/500x500?text=No+Image";

  const isOnSale = product?.on_sale === true || product?.on_sale === "true";
  const isOutOfStock = Number(product?.stock_quantity) < 1;

  const price = isOnSale ? product?.sale_price : product?.regular_price;

  const discount =
    isOnSale && product?.regular_price
      ? Math.floor(
          ((Number(product.regular_price) - Number(product.sale_price)) * 100) /
            Number(product.regular_price),
        )
      : 0;

  useEffect(() => {
    if (!product?._id) return;

    fetch(`https://ebay-backend.vercel.app/reviews?productId=${product?._id}`)
      .then((res) => res.json())
      .then((data) => setReviews(data))
      .catch((error) => console.error("Error fetching reviews:", error));
  }, [product?._id]);

  const totalRating = reviews.reduce(
    (sum, review) => sum + Number(review.rating || 0),
    0,
  );

  const avgRating =
    reviews.length > 0 ? (totalRating / reviews.length).toFixed(1) : "0.0";

  const goToDetails = () => {
    navigate(`/product/${product?.slug}`);
  };

  const handleAddToCart = (item) => {
    if (isOutOfStock) return;

    const exists = cart.find((cartItem) => cartItem._id === item._id);

    const newCart = exists
      ? cart.map((cartItem) =>
          cartItem._id === item._id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem,
        )
      : [...cart, { ...item, quantity: 1 }];

    setCart(newCart);
    addToDb(item._id);
    toast.success("ADDED TO CART");

    ReactPixel.track("AddToCart", {
      content_ids: item?._id,
      content_name: item?.name,
      content_type: "product",
      value: price,
      currency: "BDT",
    });
  };

  return (
    <>
      <div className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
        {isOnSale && discount > 0 && (
          <div className="absolute left-2 top-2 z-20 rounded-md bg-[#729B7F] px-2.5 py-1 text-[11px] font-semibold text-white sm:left-3 sm:top-3 sm:text-xs">
            -{discount}%
          </div>
        )}

        {isOutOfStock && (
          <div className="absolute right-2 top-2 z-20 rounded-md bg-gray-800 px-2.5 py-1 text-[11px] font-semibold text-white sm:right-3 sm:top-3 sm:text-xs">
            Out of Stock
          </div>
        )}

        <div className="relative flex h-[190px] items-center justify-center overflow-hidden bg-[#f8f8f8] sm:h-[230px] md:h-[260px] lg:h-[280px]">
          <button
            type="button"
            onClick={goToDetails}
            className="flex h-full w-full items-center justify-center"
          >
            <LazyLoadImage
              src={productImage}
              alt={product?.name || "Product image"}
              effect="blur"
              className="max-h-[160px] max-w-[100%] object-contain transition-transform duration-500 group-hover:scale-105 sm:max-h-[200px] md:max-h-[230px] lg:max-h-[240px]"
            />
          </button>

          <div className="pointer-events-none absolute inset-0 bg-black/40 opacity-0 transition-all duration-300 group-hover:opacity-100" />

          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 translate-y-6 gap-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 sm:left-auto sm:right-3 sm:top-1/2 sm:-translate-y-1/2 sm:translate-x-10 sm:flex-col sm:group-hover:translate-x-0 sm:group-hover:-translate-y-1/2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(true);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-gray-700 shadow-lg transition hover:bg-[#729B7F] hover:text-white sm:h-11 sm:w-11"
              aria-label="Quick view"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>

            <button
              type="button"
              onClick={goToDetails}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-gray-700 shadow-lg transition hover:bg-[#729B7F] hover:text-white sm:h-11 sm:w-11"
              aria-label="View details"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12l-7.5 7.5"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 12h18"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-3 sm:p-4">
          <h3
            onClick={goToDetails}
            className="mb-0 line-clamp-2 min-h-[44px] cursor-pointer text-sm font-medium leading-5 text-gray-800 transition hover:text-[#729B7F] sm:min-h-[52px] sm:text-[15px] sm:leading-6"
          >
            {product?.name}
          </h3>

          <div className="mb-3 flex items-center gap-2">
            <StarRating rating={avgRating} size={14} />
            <span className="text-[11px] text-gray-500 sm:text-xs">
              ({avgRating}) | {reviews.length} Reviews
            </span>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2 sm:mb-5">
            <span className="text-lg font-bold text-[#075e27] sm:text-xl">
              $ {(Number(price) * 0.0081).toFixed(2)}
            </span>

            {isOnSale && (
              <span className="text-xs text-gray-400 line-through sm:text-sm">
                $ {(Number(product?.regular_price) * 0.0081).toFixed(2)}
              </span>
            )}
          </div>
          {/* 
          <div className="mt-auto grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={goToDetails}
              className="rounded-md border border-[#729B7F] bg-white px-2 py-2 text-sm font-semibold text-[#729B7F] transition hover:bg-[#729B7F] hover:text-white sm:py-2.5"
            >
              View Details
            </button>

            <button
              type="button"
              disabled={isOutOfStock}
              onClick={() => handleAddToCart(product)}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition sm:py-2.5 ${
                !isOutOfStock
                  ? "bfg-[#729B7F] bg-info text-white hover:opacity-90"
                  : "cursor-not-allowed bg-gray-300 text-gray-500"
              }`}
            >
              {isOutOfStock ? "Stock Out" : "Add to Cart"}
            </button>
          </div> */}
          <div className="mt-auto grid grid-cols-1 gap-2 sm:grid-cols-1">
            <button
              type="button"
              onClick={goToDetails}
              className="rounded-md border border-info bg-white px-2 py-2 text-sm font-semibold text-info transition hover:bg-[#729B7F] hover:text-white sm:py-2.5"
            >
              View Details
            </button>

            {/* <button
              type="button"
              disabled={isOutOfStock}
              onClick={() => handleAddToCart(product)}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition sm:py-2.5 ${
                !isOutOfStock
                  ? "bfg-[#729B7F] bg-info text-white hover:opacity-90"
                  : "cursor-not-allowed bg-gray-300 text-gray-500"
              }`}
            >
              {isOutOfStock ? "Stock Out" : "Add to Cart"}
            </button> */}
          </div>
        </div>
      </div>

      {showImageModal && (
        <div
          onClick={() => setShowImageModal(false)}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-3 sm:p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[95vw] rounded-xl bg-white p-3 shadow-2xl sm:max-w-4xl sm:p-4"
          >
            <button
              type="button"
              onClick={() => setShowImageModal(false)}
              className="absolute -right-2 -top-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#729B7F] text-lg text-white shadow-lg sm:-right-3 sm:-top-3 sm:h-10 sm:w-10"
              aria-label="Close image modal"
            >
              ✕
            </button>

            <img
              src={productImage}
              alt={product?.name || "Product image"}
              className="mx-auto max-h-[75vh] w-full object-contain sm:max-h-[80vh]"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Product;
